/**
 * Sisumail API Client V2
 */

import {
    type AdminGetIdentityResponse,
    type AdminListIdentitiesResponse,
    type AdminLogsResponse,
    type AdminStatsResponse,
    type AuthChallengeResponse,
    type AuthProveResponse,
    type DeviceClaimResponse,
    type DeviceFinalizeResponse,
    type DeviceListResponse,
    type DevicePairResponse,
    type DeviceRequestsResponse,
    type IdentityStatusResponse,
    type InboxGetResponse,
    type InboxListResponse,
    parseRouteKey,
    type RegisterResponse,
    type ReserveUsernameResponse,
    ROUTE_KEYS,
    ROUTE_META,
    type RouteKey,
    // Request Types
    type AuthChallengeRequest,
    type AuthProveRequest,
    type DeviceClaimRequest,
    type DeviceFinalizeRequest,
    type DevicePairRequest,
    type RegisterRequest,
    type ReserveUsernameRequest,
} from '@sisumail/contracts';
import {
    ApiErrorResponse,
    ApiSuccessResponse,
    // Request types moved to contracts
} from '@sisumail/types';
import * as v from 'valibot';
import type { ClientConfig } from './client';
import {
    ApiClientError,
    type ApiResult,
    ContractClient,
    type ContractViolationError,
    deriveValidity,
    type EndpointDef,
    type Validity,
} from './client';

export {
    ApiClientError,
    ContractClient,
    ContractViolationError,
    deriveValidity,
    type ApiResult,
    type ClientConfig,
    type EndpointDef,
    type Validity,
} from './client';

// =============================================================================

// =============================================================================

/**
 * Build a path from a RouteKey, substituting :param placeholders
 */
function buildPath(key: RouteKey, params?: Record<string, string>): string {
    let { path } = parseRouteKey(key);
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            path = path.replace(`:${k}`, encodeURIComponent(v));
        }
    }
    return path;
}

export class SisumailClient extends ContractClient {
    constructor(config: ClientConfig) {
        super({
            env: 'production',
            credentials: 'include', // DIAMOND: Ensure cookies are sent by default
            ...config,
        });
    }

    /**
     * Helper to construct EndpointDef from RouteKey + Meta.
     * Guards against missing schemas which should not happen in Phase 3.
     */
    private getEndpoint<
        TReq extends v.BaseSchema<any, any, any>,
        TRes extends v.BaseSchema<any, any, any>,
    >(key: RouteKey): EndpointDef<TReq, TRes> {
        const meta = ROUTE_META[key];
        const { method, path } = parseRouteKey(key);

        if (!meta.responseSchema) {
            // Response schema is strictly required
            throw new Error(`Missing response schema for route: ${key}`);
        }

        // Strict behavior: if no requestSchema, we expect no input (undefined)
        // This prevents implicit 'any' payloads.
        const requestSchema = meta.requestSchema || v.undefined();

        return {
            alias: key,
            method: method as 'GET' | 'POST' | 'PUT' | 'DELETE',
            path,
            request: requestSchema as TReq,
            response: meta.responseSchema as TRes,
            metadata: {
                auth: meta.auth !== 'public', // simple mapping
                ...(meta.smoke !== undefined ? { smoke: meta.smoke } : {}),
                ...(meta.danger !== undefined ? { danger: meta.danger } : {}),
            },
        };
    }

    // =========================================================================
    // Auth Routes (Migrated)
    // =========================================================================
    auth = {
        reserve: (params: ReserveUsernameRequest): Promise<ApiResult<ReserveUsernameResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_RESERVE), params),

        check: (username: string): Promise<ApiResult<{ available: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHECK_USERNAME), { username }),

        challenge: (params: AuthChallengeRequest): Promise<ApiResult<AuthChallengeResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHALLENGE), params),

        prove: (params: AuthProveRequest): Promise<ApiResult<AuthProveResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_PROVE), params),

        register: (params: RegisterRequest): Promise<ApiResult<RegisterResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_REGISTER), params),

        logout: (): Promise<ApiResult<{ success: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.AUTH_LOGOUT), {}),

        listSessions: (): Promise<ApiResult<{ sessions: any[] }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_LIST), undefined),

        revokeSession: (id: string): Promise<ApiResult<{ success: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_DELETE), { id }),

        revokeOthers: (): Promise<ApiResult<{ success: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.SESSIONS_REVOKE_OTHERS), undefined),
    };

    identity = {
        status: (include_crypto?: boolean): Promise<ApiResult<IdentityStatusResponse>> => {
            const params = include_crypto ? { include_crypto: 'true' } : undefined;
            return this.call(this.getEndpoint(ROUTE_KEYS.IDENTITY_STATUS), params);
        },

        exists: async (username: string): Promise<ApiResult<{ exists: boolean }>> => {
            const result = (await this.call(this.getEndpoint(ROUTE_KEYS.AUTH_CHECK_USERNAME), {
                username,
            })) as ApiResult<{ available: boolean }>;
            return { ...result, data: { exists: !result.data.available } };
        },
    };

    // =========================================================================
    // Device Routes (Migrated)
    // =========================================================================
    devices = {
        requestPairing: (params: DevicePairRequest): Promise<ApiResult<DevicePairResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_PAIR_REQUEST), params),

        finalizeReader: (
            params: DeviceFinalizeRequest
        ): Promise<ApiResult<DeviceFinalizeResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_FINALIZE_READER), params),

        claimPairing: (params: DeviceClaimRequest): Promise<ApiResult<DeviceClaimResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_PAIR_CLAIM), params),

        listRequests: (): Promise<ApiResult<DeviceRequestsResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_REQUESTS), undefined),

        list: (): Promise<ApiResult<DeviceListResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_LIST), undefined),

        revoke: (id: string): Promise<ApiResult<{ success: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.DEVICES_REVOKE), { id }),

        /**
         * DIAMOND: Connect to the device stream (SSE)
         * Returns the raw Response object to allow streaming reading
         */
        connectStream: async (signal?: AbortSignal): Promise<Response> => {
            const { method } = parseRouteKey(ROUTE_KEYS.DEVICES_STREAM);
            // Re-implement basic path building since we removed buildPath helper (or we can keep it?)
            // buildPath was outside the class, so we can keep using it if we keep the function.
            // But we deleted the function? No, buildPath is a standalone function at line 61.
            // I should check if buildPath was deleted. My replacement targets the class body.
            // Wait, buildPath helper function was at line 61. It is outside the class.
            // I will use it.
            const path = buildPath(ROUTE_KEYS.DEVICES_STREAM);
            const url = `${this.config.baseUrl}${path}`;

            const headers: Record<string, string> = {
                Accept: 'text/event-stream',
            };

            if (this.config.deviceKey) {
                headers['Authorization'] =
                    `DeviceKey ${this.config.deviceKey.id}:${this.config.deviceKey.secret}`;
            }

            // We bypass the standard request() wrapper because we need the raw stream
            // and we don't want the default JSON parsing logic
            return fetch(url, {
                method,
                headers,
                signal: signal || null,
            });
        },
    };

    inbox = {
        list: (params: { since?: number } = {}): Promise<ApiResult<InboxListResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.INBOX_LIST), params),

        get: (id: string): Promise<ApiResult<InboxGetResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.INBOX_GET), { id }),
    };

    async log(entry: Record<string, unknown>): Promise<void> {
        await this.call(this.getEndpoint(ROUTE_KEYS.LOG_INGEST), entry);
    }

    billing = {
        checkout: (): Promise<ApiResult<{ checkout_url: string; payment_id: string }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.BILLING_CHECKOUT), {}),

        verify: (paymentId: string): Promise<ApiResult<{ success: boolean }>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.BILLING_VERIFY), { paymentId }),

        simulate: (username: string): Promise<ApiResult<any>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.BILLING_DEV_SIMULATE), { username }),
    };

    admin = {
        stats: (): Promise<ApiResult<AdminStatsResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_STATS), undefined),

        provision: (username: string) =>
            this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_PROVISION), { username }),

        listIdentities: (
            limit = 50,
            offset = 0,
            search?: string
        ): Promise<ApiResult<AdminListIdentitiesResponse>> => {
            return this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_LIST_IDENTITIES), {
                limit,
                offset,
                search,
            });
        },

        getIdentity: (username: string): Promise<ApiResult<AdminGetIdentityResponse>> =>
            this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_GET_IDENTITY), { username }),

        activateIdentity: (username: string) =>
            this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_ACTIVATE_IDENTITY), { username }),

        revokeIdentity: (username: string) =>
            this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_DELETE_IDENTITY), { username }),

        logs: (
            limit = 100,
            offset = 0,
            level?: string,
            source?: string,
            requestId?: string,
            userId?: string
        ): Promise<ApiResult<AdminLogsResponse>> => {
            return this.call(this.getEndpoint(ROUTE_KEYS.ADMIN_LOGS), {
                limit,
                offset,
                level,
                source,
                requestId,
                userId,
            });
        },
    };
}

// ApiClientError imported from ./client

export function createClient(config: ClientConfig): SisumailClient {
    return new SisumailClient(config);
}

/**
 * DIAMOND: Standardized client factory with consistent defaults.
 * Use this instead of createClient for consistent behavior across all frontends.
 */
export interface SisumailClientOptions {
    /** Base URL of the API */
    baseUrl: string;
    /** Environment mode - affects validation strictness */
    env: 'development' | 'preview' | 'production';
    /** Called when API returns an error */
    onError?: (error: { code: string; message: string }) => void;
    /** Called on contract version mismatch - should show "Update Required" UI */
    onVersionMismatch?: () => void;
    /** Called on schema violations - should log/report */
    onSchemaViolation?: (error: ContractViolationError) => void;
    /** Device key for device-plane auth */
    deviceKey?: { id: string; secret: string };
    /** Admin secret for admin-plane auth */
    adminSecret?: string;
    /** Add custom headers to each request */
    customHeaders?: Record<string, string>;
    /** Custom request hook */
    onRequest?: (init: RequestInit) => RequestInit;
}

export function createSisumailClient(options: SisumailClientOptions): SisumailClient {
    const config: ClientConfig = {
        baseUrl: options.baseUrl,
        env: options.env,
        onRequest: (init) => {
            let h = (init.headers || {}) as Record<string, string>;

            // Add custom headers
            if (options.customHeaders) {
                h = { ...h, ...options.customHeaders };
            }

            // Add admin auth if provided
            if (options.adminSecret) {
                h['Authorization'] = `Bearer ${options.adminSecret}`;
            }

            init.headers = h;

            // Chain user-provided onRequest
            if (options.onRequest) {
                init = options.onRequest(init);
            }

            return init;
        },
        onContractViolation: (error) => {
            // Version mismatch gets special handling
            if (error.name === 'ContractVersionMismatchError') {
                if (options.onVersionMismatch) {
                    options.onVersionMismatch();
                } else {
                    console.warn('[API] Version mismatch - update required');
                }
                return;
            }

            // Standard schema violation
            if (options.onSchemaViolation) {
                options.onSchemaViolation(error);
            } else {
                console.error('[API] Schema violation:', error.getSummary?.() || error.message);
            }
        },
    };

    // Add optional properties only if defined
    if (options.deviceKey) {
        config.deviceKey = options.deviceKey;
    }
    if (options.onError) {
        config.onError = options.onError;
    }

    return new SisumailClient(config);
}

export default SisumailClient;
